import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookOpen,
  Clock,
  Users,
  Star,
  Play,
  FileText,
  Download,
  CheckCircle,
  Lock,
  ArrowLeft,
  Calendar,
  Award,
  Video,
  File,
  Link as LinkIcon,
  ChevronDown,
  ChevronRight,
  Eye,
  Menu,
  X,
} from "lucide-react";
import ReactPlayer from "react-player";
// Use react-doc-viewer for inline document previews
// @ts-ignore - some versions of react-doc-viewer don't provide TypeScript types
import DocViewer, { DocViewerRenderers } from "react-doc-viewer";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { eCourseService } from "../../services/elearning/eCourseService";
import StarRating from "../../components/common/StarRating";
import ReviewForm from "../../components/elearning/ReviewForm";
import CourseDiscussionBoard from "../../pages/elearning/CourseDiscussionBoard";
// Import Quiz components for inline quiz display
import { QuizTaking, QuizResults } from "../../components/elearning/quiz";
import QuizDetails from "../../components/elearning/quiz/QuizDetails";
import quizService from "../../services/quizService";

interface CourseModule {
  _id: string;
  title: string;
  description: string;
  order: number;
  quizId?: string;
  lessons: CourseLesson[];
  isCompleted: boolean;
  quizCompleted?: boolean;
  isLocked: boolean;
  isAccessible?: boolean;
}

interface CourseLesson {
  _id: string;
  title: string;
  description: string;
  type: "video" | "text" | "quiz" | "assignment" | "document" | "mixed";
  duration?: number;
  content: string;
  videoUrl?: string;
  fileUrl?: string; // For document lessons
  quizId?: string; // For quiz lessons
  // Mixed content support
  mediaUrls?: string[];
  externalUrls?: string[];
  contentItems?: ContentItem[];
  attachments?: CourseMaterial[];
  isCompleted: boolean;
  isLocked: boolean;
  order: number;
}

interface ContentItem {
  id: string;
  type:
    | "video"
    | "document"
    | "image"
    | "audio"
    | "text"
    | "link"
    | "interactive";
  title: string;
  content?: string;
  mediaUrl?: string;
  externalUrl?: string;
  files?: {
    fileUrl: string;
    fileName: string;
    fileSize: number;
  }[];
}

interface CourseMaterial {
  _id: string;
  title: string;
  description?: string;
  type: "pdf" | "video" | "image" | "document" | "link" | "file";
  fileUrl: string;
  fileSize?: number;
  createdAt: string;
}

interface CourseDetails {
  _id: string;
  title: string;
  description: string;
  instructor: {
    _id: string;
    firstName: string;
    lastName: string;
    avatar?: string;
  };
  category: string;
  level: "beginner" | "intermediate" | "advanced";
  duration:
    | number
    | {
        estimatedHours: number;
        estimatedMinutes: number;
      };
  modules: CourseModule[];
  materials: CourseMaterial[];
  totalStudents: number;
  rating: number;
  reviewCount: number;
  progress: {
    completedLessons: number;
    totalLessons: number;
    percentage: number;
  };
  enrollmentDate: string;
  isEnrolled: boolean;
  enrollmentStatus?: "enrolled" | "completed";
  chain?: {
    chainId?: string;
    sequenceNumber?: number;
    isChainFinal?: boolean;
    nextCourseId?: string;
  };
}

// Helper function to format file size in human-readable form
function formatFileSize(size?: number): string {
  if (!size || isNaN(size)) return "";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let fileSize = size;
  while (fileSize >= 1024 && i < units.length - 1) {
    fileSize /= 1024;
    i++;
  }
  return `${fileSize.toFixed(1)} ${units[i]}`;
}

// Helper function to detect and convert YouTube URLs
function toYouTubeEmbedUrl(videoUrl: string): string {
  if (!videoUrl || typeof videoUrl !== "string") return "";
  const cleanUrl = videoUrl.trim();

  let videoId = "";

  // If it's already an embed URL, extract the ID
  const embedMatch = cleanUrl.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{11})/);
  if (embedMatch && embedMatch[1]) {
    videoId = embedMatch[1];
  }

  // If it's a youtu.be link
  if (!videoId) {
    const shortMatch = cleanUrl.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
    if (shortMatch && shortMatch[1]) videoId = shortMatch[1];
  }

  // If it's a shorts link
  if (!videoId) {
    const shortsMatch = cleanUrl.match(
      /youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/
    );
    if (shortsMatch && shortsMatch[1]) videoId = shortsMatch[1];
  }

  // Try to extract v= param anywhere in the string
  if (!videoId) {
    const vParamMatch =
      cleanUrl.match(/[?&]v=([A-Za-z0-9_-]{11})/i) ||
      cleanUrl.match(/\bv=([A-Za-z0-9_-]{11})\b/i);
    if (vParamMatch && vParamMatch[1]) videoId = vParamMatch[1];
  }

  // If input is just a query string like "v=ID&..."
  if (!videoId) {
    try {
      const queryOnly =
        cleanUrl.startsWith("?") || /^[A-Za-z0-9_=&-]+$/.test(cleanUrl);
      if (queryOnly) {
        const params = new URLSearchParams(cleanUrl.replace(/^\?+/, ""));
        const v = params.get("v");
        if (v && v.length === 11) videoId = v;
      }
    } catch (e) {
      // ignore
    }
  }

  // Last resort: find any 11-char candidate
  if (!videoId) {
    const anyId = cleanUrl.match(/([A-Za-z0-9_-]{11})/);
    if (anyId && anyId[1]) videoId = anyId[1];
  }

  // If we found a video ID, return embed URL with parameters to minimize branding
  if (videoId) {
    const params = new URLSearchParams({
      modestbranding: "1", // Minimize YouTube branding
      rel: "0", // Don't show related videos from other channels
      showinfo: "0", // Hide video title and uploader before play
      controls: "1", // Show player controls
      fs: "1", // Allow fullscreen
      cc_load_policy: "0", // Don't show captions by default
      iv_load_policy: "3", // Hide video annotations
      autohide: "1", // Auto-hide controls
      color: "white", // Use white progress bar
      disablekb: "0", // Enable keyboard controls
      enablejsapi: "1", // Enable JavaScript API
      playsinline: "1", // Play inline on iOS
    });
    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
  }

  // Nothing found - return original
  return cleanUrl;
}

// Helper function to check if URL is a YouTube video
function isYouTubeUrl(url: string): boolean {
  if (!url) return false;
  return /(?:youtube\.com|youtu\.be)/i.test(url);
}

// Helper function to check if URL is a Vimeo video
function isVimeoUrl(url: string): boolean {
  if (!url) return false;
  return /(?:vimeo\.com)/i.test(url);
}

// Helper function to convert Vimeo URLs to embed format
function toVimeoEmbedUrl(videoUrl: string): string {
  if (!videoUrl || typeof videoUrl !== "string") return "";
  const cleanUrl = videoUrl.trim();

  // Extract video ID from various Vimeo URL formats
  const vimeoMatch = cleanUrl.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch && vimeoMatch[1]) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  return cleanUrl; // Return original if no match
}

const StudentCourseViewer: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedLesson, setSelectedLesson] = useState<CourseLesson | null>(
    null
  );
  const [activeTab, setActiveTab] = useState<
    "content" | "overview" | "discussions"
  >("content");
  const [expandedModules, setExpandedModules] = useState<Set<string>>(
    new Set()
  );
  const [previewDocument, setPreviewDocument] = useState<CourseMaterial | null>(
    null
  );
  const [reviews, setReviews] = useState<any[]>([]);
  const [ratingSummary, setRatingSummary] = useState<any>(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [userReview, setUserReview] = useState<any>(null);
  const [certificate, setCertificate] = useState<any>(null);
  const [isGeneratingCertificate, setIsGeneratingCertificate] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Mobile sidebar toggle
  const [expandedContentItems, setExpandedContentItems] = useState<Set<string>>(
    new Set()
  );
  const [expandedLessonSections, setExpandedLessonSections] = useState<
    Set<string>
  >(
    new Set(["main-content"]) // Main content expanded by default
  );
  const [expandedAttachments, setExpandedAttachments] = useState<Set<string>>(
    new Set()
  );
  // Quiz display state
  const [showInlineQuiz, setShowInlineQuiz] = useState(false);
  const [showInlineQuizResults, setShowInlineQuizResults] = useState(false);
  const [currentQuizAttempt, setCurrentQuizAttempt] = useState<any>(null);
  const [selectedModuleQuiz, setSelectedModuleQuiz] = useState<any>(null);
  const [videoLoadError, setVideoLoadError] = useState(false);

  // Handle viewing quiz results inline
  const handleViewQuizResults = async (quizId: string) => {
    try {
      const attempts = await quizService.getStudentAttempts(quizId);
      if (attempts.length > 0) {
        // Show the latest attempt
        const latestAttempt = attempts[0];
        setCurrentQuizAttempt(latestAttempt);
        setShowInlineQuizResults(true);
        setShowInlineQuiz(false);
      }
    } catch (error) {
      console.error("Error loading quiz attempts:", error);
    }
  };

  // Fetch course details
  const { data: courseData, isLoading } = useQuery({
    queryKey: ["course-details", courseId],
    queryFn: () => eCourseService.getCourseContent(courseId!),
    enabled: !!courseId,
  });

  const course: CourseDetails | undefined = courseData?.data;

  // Fetch chain courses if this course is part of a chain
  const { data: chainCoursesData } = useQuery({
    queryKey: ["chain-courses", course?.chain?.chainId],
    queryFn: () => eCourseService.getPublicCourses({ limit: 100 }), // Fetch more courses to find chain
    enabled: !!course?.chain?.chainId,
  });

  // Find the next course in the chain
  const nextCourseInChain = React.useMemo(() => {
    if (!course?.chain?.chainId || !chainCoursesData?.data?.courses)
      return null;

    const chainCourses = chainCoursesData.data.courses.filter(
      (c: any) => c.chain?.chainId === course.chain?.chainId
    );

    const nextSequence = (course.chain?.sequenceNumber || 0) + 1;
    return (
      chainCourses.find((c: any) => c.chain?.sequenceNumber === nextSequence) ||
      null
    );
  }, [course?.chain, chainCoursesData]);

  // Update selectedLesson when course data changes
  React.useEffect(() => {
    if (selectedLesson && course) {
      // Find the updated lesson in the new course data
      for (const module of course.modules || []) {
        const updatedLesson = module.lessons?.find(
          (l) => l._id === selectedLesson._id
        );
        if (updatedLesson) {
          setSelectedLesson(updatedLesson);
          break;
        }
      }
    }
  }, [course, selectedLesson?._id]);

  // Reset video load error when lesson changes
  React.useEffect(() => {
    setVideoLoadError(false);
  }, [selectedLesson]);

  // Expand first module by default
  React.useEffect(() => {
    if (courseData?.data?.modules?.length > 0) {
      setExpandedModules(
        new Set(
          [courseData?.data?.modules?.[0]?._id].filter(Boolean) as string[]
        )
      );
    }
  }, [courseData]);

  // Load reviews when course is loaded
  React.useEffect(() => {
    if (courseId && course) {
      loadReviews();
    }
  }, [courseId, course]);

  // Expand first content item when lesson changes (for mixed content with multiple items)
  React.useEffect(() => {
    if (
      selectedLesson?.type === "mixed" &&
      selectedLesson.contentItems &&
      selectedLesson.contentItems.length > 0
    ) {
      // Only expand first item by default
      const firstItemId = selectedLesson.contentItems[0]?.id;
      if (firstItemId) {
        setExpandedContentItems(new Set([firstItemId]));
      }
    }

    // Reset lesson sections when lesson changes - main content always expanded
    setExpandedLessonSections(new Set(["main-content"]));
  }, [selectedLesson?._id, selectedLesson?.type]);

  const markLessonComplete = async (lessonId: string) => {
    if (!course) return;

    try {
      // Find the module and lesson
      let targetModule: CourseModule | undefined;

      for (const module of course.modules) {
        const lesson = module.lessons.find((l) => l._id === lessonId);
        if (lesson) {
          targetModule = module;
          break;
        }
      }

      if (!targetModule) {
        console.error("Module not found for lesson");
        return;
      }

      // Call API to mark lesson as complete
      await eCourseService.updateContentProgress(
        courseId!,
        targetModule._id,
        lessonId,
        {
          status: "completed",
        }
      );

      // Refetch course data to update progress and completion status
      await queryClient.invalidateQueries({
        queryKey: ["course-details", courseId],
        refetchType: "active",
      });
    } catch (error) {
      console.error("Error marking lesson complete:", error);
    }
  };

  const toggleModuleExpansion = (moduleId: string) => {
    setExpandedModules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  };

  const toggleContentItemExpansion = (itemId: string) => {
    setExpandedContentItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const toggleLessonSection = (sectionId: string) => {
    setExpandedLessonSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const toggleAttachment = (attachmentId: string) => {
    setExpandedAttachments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(attachmentId)) {
        newSet.delete(attachmentId);
      } else {
        newSet.add(attachmentId);
      }
      return newSet;
    });
  };

  const downloadMaterial = (material: CourseMaterial) => {
    if (material.type === "link") {
      window.open(material.fileUrl, "_blank");
    } else {
      // Handle file download
      const link = document.createElement("a");
      link.href = material.fileUrl;
      link.download = material.title;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getMaterialIcon = (type: string) => {
    switch (type) {
      case "video":
        return <Video className="w-4 h-4" />;
      case "pdf":
      case "document":
        return <FileText className="w-4 h-4" />;
      case "link":
        return <LinkIcon className="w-4 h-4" />;
      default:
        return <File className="w-4 h-4" />;
    }
  };

  const getLessonIcon = (type: string) => {
    switch (type) {
      case "video":
        return <Play className="w-4 h-4" />;
      case "quiz":
        return <Award className="w-4 h-4" />;
      case "assignment":
        return <FileText className="w-4 h-4" />;
      default:
        return <BookOpen className="w-4 h-4" />;
    }
  };

  const loadReviews = async () => {
    if (!courseId) return;
    try {
      const [reviewsResponse, summaryResponse] = await Promise.all([
        eCourseService.getCourseRatings(courseId),
        eCourseService.getCourseRatingSummary(courseId),
      ]);

      setReviews(reviewsResponse.data.ratings || []);
      setRatingSummary(summaryResponse.data);

      // Check if user has already reviewed
      const userReview = reviewsResponse.data.ratings?.find(
        (review: any) =>
          review.userId === JSON.parse(localStorage.getItem("user") || "{}")._id
      );
      setUserReview(userReview || null);
    } catch (error) {
      console.error("Error loading reviews:", error);
    }
  };

  // Load certificate for completed course
  const loadCertificate = async () => {
    if (!courseId || !(course?.enrollmentStatus === "completed")) return;

    try {
      // Check if certificate exists for this course
      const certificatesResponse =
        await eCourseService.getStudentCertificates();
      const courseCertificate = certificatesResponse.data.find(
        (cert: any) => cert.courseId === courseId
      );

      if (courseCertificate) {
        setCertificate(courseCertificate);
      }
    } catch (error) {
      console.error("Error loading certificate:", error);
    }
  };

  // Generate certificate
  const generateCertificate = async () => {
    if (!courseId) return;

    setIsGeneratingCertificate(true);
    try {
      const response = await eCourseService.generateCertificate(courseId);
      setCertificate(response.data);
    } catch (error) {
      console.error("Error generating certificate:", error);
    } finally {
      setIsGeneratingCertificate(false);
    }
  };

  // Download certificate
  const downloadCertificate = async () => {
    if (!certificate) return;

    try {
      const response = await eCourseService.downloadCertificate(
        certificate._id
      );
      // Open download URL in new tab
      window.open(response.data.downloadUrl, "_blank");
    } catch (error) {
      console.error("Error downloading certificate:", error);
    }
  };

  // Load certificate when course data changes
  React.useEffect(() => {
    if (course?.enrollmentStatus === "completed") {
      loadCertificate();
    }
  }, [course?.enrollmentStatus, courseId]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!course) {
    return (
      <div className="p-6 text-center">
        <BookOpen className="w-16 h-16 text-slate-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          Course not found
        </h2>
        <p className="text-slate-600 mb-4">
          The course you're looking for doesn't exist or you don't have access
          to it.
        </p>
        <button
          onClick={() => navigate("/student/elearning/catalog")}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Browse Courses
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40">
      {/* Mobile Header - Modern Glassmorphism Design */}
      <div className="lg:hidden bg-white/90 backdrop-blur-2xl border-b border-slate-200/50 sticky top-0 z-40 shadow-sm">
        {/* Top Row - Navigation */}
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 hover:bg-slate-100/80 rounded-xl transition-all duration-200 active:scale-95"
          >
            <Menu className="w-5 h-5 text-slate-700" />
          </button>
          <div className="flex-1 mx-3 text-center">
            <h1 className="text-sm font-bold text-slate-900 truncate">
              {course.title}
            </h1>
            <p className="text-xs text-slate-500 truncate">
              {course.instructor.firstName} {course.instructor.lastName}
            </p>
          </div>
          <button
            onClick={() => navigate("/student/elearning/dashboard")}
            className="p-2 hover:bg-slate-100/80 rounded-xl transition-all duration-200 active:scale-95"
          >
            <X className="w-5 h-5 text-slate-700" />
          </button>
        </div>

        {/* Progress Bar - Always visible on mobile */}
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-slate-600">
              {course.progress.completedLessons} of{" "}
              {course.progress.totalLessons} lessons
            </span>
            <span className="text-xs font-bold text-blue-600">
              {course.progress.percentage}%
            </span>
          </div>
          <div className="relative w-full bg-slate-200/60 rounded-full h-1.5 overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${course.progress.percentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Overlay for mobile sidebar */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Modern Apple-Style Navigation */}
      <div
        className={`
          fixed lg:relative 
          top-0 left-0 
          h-full 
          w-[85vw] max-w-[320px] lg:w-80 lg:max-w-none
          bg-white/98 backdrop-blur-2xl
          border-r border-slate-200/50
          flex flex-col 
          transform transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
          z-50 lg:z-0
          shadow-2xl lg:shadow-none
          ${
            isSidebarOpen
              ? "translate-x-0"
              : "-translate-x-full lg:translate-x-0"
          }
        `}
      >
        {/* Header - Elegant & Clean */}
        <div className="p-4 lg:p-5 border-b border-slate-100/80 bg-gradient-to-b from-white to-slate-50/50">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate("/student/elearning/dashboard")}
              className="hidden lg:flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors duration-200 group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
              <span className="text-sm font-medium">Dashboard</span>
            </button>
            {/* Close button for mobile */}
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="lg:hidden p-2 hover:bg-slate-100 rounded-xl transition-all duration-200 ml-auto"
            >
              <X className="w-5 h-5 text-slate-600" />
            </button>
          </div>
          <h2 className="font-bold text-slate-900 line-clamp-2 text-base leading-tight">
            {course.title}
          </h2>
          <div className="flex items-center gap-2 text-sm text-slate-600 mt-2">
            <span className="font-medium">
              {course.instructor.firstName} {course.instructor.lastName}
            </span>
          </div>

          {/* Learning Path Badge */}
          {course.chain?.chainId && (
            <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-blue-900">
                    Part {course.chain.sequenceNumber} of Learning Series
                  </p>
                  <p className="text-xs text-blue-700">
                    {course.chain.isChainFinal
                      ? "🏆 Final Course"
                      : "📖 Intermediate Course"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Progress - Modern Circular Design */}
        <div className="p-4 lg:p-5 border-b border-slate-100/80 bg-gradient-to-br from-slate-50/80 to-blue-50/50">
          <div className="flex items-center gap-4">
            {/* Circular Progress */}
            <div className="relative w-14 h-14 flex-shrink-0">
              <svg className="w-14 h-14 transform -rotate-90">
                <circle
                  cx="28"
                  cy="28"
                  r="24"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  className="text-slate-200"
                />
                <circle
                  cx="28"
                  cy="28"
                  r="24"
                  stroke="url(#progressGradient)"
                  strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${course.progress.percentage * 1.51} 151`}
                  className="transition-all duration-700 ease-out"
                />
                <defs>
                  <linearGradient
                    id="progressGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#8b5cf6" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold text-slate-800">
                  {course.progress.percentage}%
                </span>
              </div>
            </div>
            {/* Progress Text */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800">
                Your Progress
              </p>
              <p className="text-xs text-slate-600 mt-0.5">
                {course.progress.completedLessons} of{" "}
                {course.progress.totalLessons} lessons completed
              </p>
              {course.progress.percentage === 100 && (
                <div className="flex items-center gap-1 mt-1">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  <span className="text-xs font-medium text-green-600">
                    Course Complete!
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Course Modules - Modern Cards */}
        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-2 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
          {course.modules.map((module, moduleIndex) => {
            const isExpanded = expandedModules.has(module._id);
            const completedLessons =
              module.lessons?.filter((l) => l.isCompleted).length || 0;
            const totalLessons = module.lessons?.length || 0;
            const moduleProgress =
              totalLessons > 0
                ? Math.round((completedLessons / totalLessons) * 100)
                : 0;

            return (
              <div
                key={module._id}
                className={`bg-white rounded-xl border overflow-hidden transition-all duration-300 ${
                  module.isCompleted
                    ? "border-green-200/60 bg-gradient-to-r from-green-50/30 to-emerald-50/30"
                    : isExpanded
                    ? "border-blue-200/60 shadow-md ring-1 ring-blue-100"
                    : "border-slate-200/60 hover:border-slate-300/60 hover:shadow-sm"
                }`}
              >
                {/* Module Header */}
                <button
                  onClick={() => toggleModuleExpansion(module._id)}
                  disabled={module.isLocked}
                  className={`w-full p-3.5 flex items-center gap-3 transition-all duration-200 ${
                    module.isLocked
                      ? "cursor-not-allowed opacity-60"
                      : "hover:bg-slate-50/50 active:bg-slate-100/50"
                  }`}
                >
                  {/* Module Number/Status Indicator */}
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-bold ${
                      module.isCompleted
                        ? "bg-green-100 text-green-600"
                        : module.isLocked
                        ? "bg-slate-100 text-slate-400"
                        : "bg-blue-100 text-blue-600"
                    }`}
                  >
                    {module.isCompleted ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : module.isLocked ? (
                      <Lock className="w-4 h-4" />
                    ) : (
                      moduleIndex + 1
                    )}
                  </div>

                  {/* Module Info */}
                  <div className="flex-1 min-w-0 text-left">
                    <h3
                      className={`font-semibold text-sm leading-tight line-clamp-1 ${
                        module.isLocked ? "text-slate-400" : "text-slate-800"
                      }`}
                    >
                      {module.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-500">
                        {totalLessons} lesson{totalLessons !== 1 ? "s" : ""}
                      </span>
                      {!module.isLocked && totalLessons > 0 && (
                        <>
                          <span className="text-slate-300">•</span>
                          <span
                            className={`text-xs font-medium ${
                              moduleProgress === 100
                                ? "text-green-600"
                                : "text-slate-500"
                            }`}
                          >
                            {moduleProgress}% done
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Expand Icon */}
                  <div
                    className={`transition-transform duration-200 ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  >
                    <ChevronDown
                      className={`w-4 h-4 ${
                        module.isLocked ? "text-slate-300" : "text-slate-400"
                      }`}
                    />
                  </div>
                </button>

                {/* Module Progress Bar */}
                {!module.isLocked && totalLessons > 0 && (
                  <div className="px-3.5 pb-2">
                    <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          moduleProgress === 100
                            ? "bg-gradient-to-r from-green-400 to-emerald-500"
                            : "bg-gradient-to-r from-blue-400 to-indigo-500"
                        }`}
                        style={{ width: `${moduleProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Expanded Lessons */}
                {isExpanded && !module.isLocked && (
                  <div className="border-t border-slate-100/80 bg-slate-50/30">
                    <div className="p-2 space-y-0.5">
                      {module.lessons?.map((lesson, lessonIndex) => (
                        <button
                          key={lesson._id}
                          onClick={() => {
                            setSelectedLesson(lesson);
                            setSelectedModuleQuiz(null);
                            setShowInlineQuiz(false);
                            setShowInlineQuizResults(false);
                            setIsSidebarOpen(false);
                          }}
                          className={`w-full p-2.5 rounded-lg flex items-center gap-3 transition-all duration-200 group ${
                            selectedLesson?._id === lesson._id
                              ? "bg-blue-100/80 text-blue-700"
                              : lesson.isCompleted
                              ? "bg-green-50/50 hover:bg-green-100/50 text-slate-700"
                              : "hover:bg-white text-slate-600 hover:text-slate-800"
                          }`}
                        >
                          {/* Lesson Status Icon */}
                          <div
                            className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${
                              lesson.isCompleted
                                ? "bg-green-100 text-green-600"
                                : selectedLesson?._id === lesson._id
                                ? "bg-blue-200 text-blue-700"
                                : "bg-slate-100 text-slate-400 group-hover:bg-slate-200"
                            }`}
                          >
                            {lesson.isCompleted ? (
                              <CheckCircle className="w-3.5 h-3.5" />
                            ) : (
                              getLessonIcon(lesson.type)
                            )}
                          </div>

                          {/* Lesson Info */}
                          <div className="flex-1 min-w-0 text-left">
                            <p className="text-sm font-medium truncate">
                              {lesson.title}
                            </p>
                            {lesson.duration && (
                              <p className="text-xs text-slate-400 mt-0.5">
                                {lesson.duration} min
                              </p>
                            )}
                          </div>

                          {/* Lesson Type Badge */}
                          <span
                            className={`text-[10px] font-medium uppercase px-1.5 py-0.5 rounded ${
                              lesson.type === "video"
                                ? "bg-purple-100 text-purple-600"
                                : lesson.type === "quiz"
                                ? "bg-amber-100 text-amber-600"
                                : lesson.type === "document"
                                ? "bg-cyan-100 text-cyan-600"
                                : "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {lesson.type}
                          </span>
                        </button>
                      ))}

                      {/* Module Quiz Button */}
                      {module.quizId && (
                        <button
                          onClick={() => {
                            setSelectedModuleQuiz(module);
                            setSelectedLesson(null);
                            setShowInlineQuiz(false);
                            setShowInlineQuizResults(false);
                            setIsSidebarOpen(false);
                          }}
                          className={`w-full p-2.5 rounded-lg flex items-center gap-3 transition-all duration-200 ${
                            selectedModuleQuiz?._id === module._id
                              ? "bg-amber-100/80 text-amber-700"
                              : module.quizCompleted
                              ? "bg-green-50/50 hover:bg-green-100/50 text-slate-700"
                              : "hover:bg-amber-50/50 text-slate-600"
                          }`}
                        >
                          <div
                            className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${
                              module.quizCompleted
                                ? "bg-green-100 text-green-600"
                                : "bg-amber-100 text-amber-600"
                            }`}
                          >
                            {module.quizCompleted ? (
                              <CheckCircle className="w-3.5 h-3.5" />
                            ) : (
                              <Award className="w-3.5 h-3.5" />
                            )}
                          </div>
                          <span className="text-sm font-medium flex-1 text-left">
                            Module Assessment
                          </span>
                          <span className="text-[10px] font-medium uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-600">
                            Quiz
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Content Area - Modern & Clean */}
      <div className="flex-1 flex flex-col overflow-hidden bg-white lg:rounded-tl-3xl lg:border-l border-slate-200/50">
        {/* Content Header - Modern Tabs */}
        <div className="bg-white border-b border-slate-200/60 px-4 lg:px-6 py-3 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex space-x-1 bg-slate-100/70 p-1 rounded-xl">
              <button
                onClick={() => setActiveTab("content")}
                className={`px-4 lg:px-5 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  activeTab === "content"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-slate-600 hover:text-slate-900 hover:bg-white/50"
                }`}
              >
                Content
              </button>
              <button
                onClick={() => setActiveTab("overview")}
                className={`px-4 lg:px-5 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  activeTab === "overview"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-slate-600 hover:text-slate-900 hover:bg-white/50"
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab("discussions")}
                className={`px-4 lg:px-5 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  activeTab === "discussions"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-slate-600 hover:text-slate-900 hover:bg-white/50"
                }`}
              >
                Discussions
              </button>
            </div>
          </div>
        </div>

        {/* Content Area - Spacious & Modern */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === "content" && (
            <div className="px-4 lg:px-8 py-6 lg:py-8 max-w-5xl mx-auto">
              {selectedModuleQuiz ? (
                <div>
                  {/* Module Quiz Header */}
                  <div className="mb-6 lg:mb-8">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-200/50">
                        <Award className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-500">
                          Module Assessment
                        </p>
                        <h1 className="text-xl lg:text-2xl font-bold text-slate-900">
                          {selectedModuleQuiz.moduleTitle}
                        </h1>
                      </div>
                    </div>
                    <p className="text-slate-600 text-base">
                      Complete this quiz to unlock the next module and track
                      your progress.
                    </p>
                  </div>

                  {/* Module Quiz Content Card */}
                  <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden shadow-sm">
                    <div className="p-4 lg:p-6">
                      {showInlineQuizResults && currentQuizAttempt ? (
                        <QuizResults
                          attempt={currentQuizAttempt}
                          quizId={selectedModuleQuiz.quizId}
                          onBackToQuiz={() => {
                            setShowInlineQuizResults(false);
                            setShowInlineQuiz(true);
                          }}
                        />
                      ) : showInlineQuiz ? (
                        <QuizTaking
                          quizId={selectedModuleQuiz.quizId}
                          onSubmit={(attempt) => {
                            setCurrentQuizAttempt(attempt);
                            setShowInlineQuiz(false);
                            setShowInlineQuizResults(true);
                            queryClient.invalidateQueries({
                              queryKey: ["course-details", courseId],
                            });
                          }}
                        />
                      ) : (
                        <QuizDetails
                          quizId={selectedModuleQuiz.quizId}
                          onStartQuiz={() => {
                            setShowInlineQuiz(true);
                            setShowInlineQuizResults(false);
                          }}
                          onViewResults={() =>
                            handleViewQuizResults(selectedModuleQuiz.quizId)
                          }
                          inline={true}
                          courseId={courseId}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ) : selectedLesson ? (
                <div>
                  {/* Lesson Header - Compact on Mobile */}
                  <div className="mb-6 lg:mb-8">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span
                            className={`text-xs font-semibold uppercase px-2 py-1 rounded-md ${
                              selectedLesson.type === "video"
                                ? "bg-purple-100 text-purple-700"
                                : selectedLesson.type === "quiz"
                                ? "bg-amber-100 text-amber-700"
                                : selectedLesson.type === "document"
                                ? "bg-cyan-100 text-cyan-700"
                                : selectedLesson.type === "mixed"
                                ? "bg-indigo-100 text-indigo-700"
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {selectedLesson.type}
                          </span>
                          {selectedLesson.isCompleted && (
                            <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                              <CheckCircle className="w-3.5 h-3.5" />
                              Completed
                            </span>
                          )}
                        </div>
                        <h1 className="text-xl lg:text-2xl font-bold text-slate-900 leading-tight">
                          {selectedLesson.title}
                        </h1>
                        {selectedLesson.description && (
                          <p className="text-slate-600 text-sm lg:text-base mt-2 leading-relaxed">
                            {selectedLesson.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Lesson Actions - Mobile Optimized */}
                    <div className="flex flex-wrap items-center gap-3">
                      {selectedLesson.duration && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Clock className="w-4 h-4" />
                          <span>{selectedLesson.duration} min</span>
                        </div>
                      )}
                      {!selectedLesson.isCompleted && (
                        <button
                          onClick={() => markLessonComplete(selectedLesson._id)}
                          className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200 text-sm font-semibold shadow-sm hover:shadow-md active:scale-[0.98]"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Mark as Complete
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Lesson Content - Modern Quiz Card */}
                  {selectedLesson.type === "quiz" ? (
                    <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden shadow-lg mb-6">
                      {/* Quiz Header */}
                      <div className="px-8 py-6 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-purple-100">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-purple-600 text-white">
                            <Award className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-slate-900">
                              Quiz: {selectedLesson.title}
                            </h3>
                            <p className="text-sm text-slate-600 mt-0.5">
                              {selectedLesson.description}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Quiz Content */}
                      <div className="p-8">
                        {showInlineQuizResults &&
                        currentQuizAttempt &&
                        selectedLesson.quizId ? (
                          <QuizResults
                            attempt={currentQuizAttempt}
                            quizId={selectedLesson.quizId}
                            onBackToQuiz={() => {
                              setShowInlineQuizResults(false);
                              setShowInlineQuiz(true);
                            }}
                          />
                        ) : showInlineQuizResults &&
                          currentQuizAttempt &&
                          !selectedLesson.quizId ? (
                          <div className="text-center py-8">
                            <Award className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-slate-900 mb-2">
                              Quiz Results Not Available
                            </h3>
                            <p className="text-slate-600">
                              This quiz lesson has not been properly configured
                              yet. Please contact your instructor.
                            </p>
                          </div>
                        ) : showInlineQuiz && selectedLesson.quizId ? (
                          <QuizTaking
                            quizId={selectedLesson.quizId}
                            onSubmit={(attempt) => {
                              setCurrentQuizAttempt(attempt);
                              setShowInlineQuiz(false);
                              setShowInlineQuizResults(true);
                              // Invalidate course data to update completion status
                              queryClient.invalidateQueries({
                                queryKey: ["course-details", courseId],
                              });
                            }}
                          />
                        ) : showInlineQuiz && !selectedLesson.quizId ? (
                          <div className="text-center py-8">
                            <Award className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-slate-900 mb-2">
                              Quiz Not Available
                            </h3>
                            <p className="text-slate-600">
                              This quiz lesson has not been properly configured
                              yet. Please contact your instructor.
                            </p>
                          </div>
                        ) : selectedLesson.quizId ? (
                          <QuizDetails
                            quizId={selectedLesson.quizId}
                            onStartQuiz={() => {
                              setShowInlineQuiz(true);
                              setShowInlineQuizResults(false);
                            }}
                            onViewResults={() =>
                              handleViewQuizResults(selectedLesson.quizId)
                            }
                            inline={true}
                            courseId={courseId}
                          />
                        ) : (
                          <div className="text-center py-8">
                            <Award className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-slate-900 mb-2">
                              Quiz Not Configured
                            </h3>
                            <p className="text-slate-600">
                              This quiz lesson has not been properly configured
                              yet. Please contact your instructor.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : selectedLesson.type === "video" ? (
                    <div className="bg-white rounded-xl lg:rounded-2xl border border-slate-200/60 overflow-hidden mb-6 shadow-sm">
                      {/* Collapsible Header */}
                      <button
                        onClick={() => toggleLessonSection("main-content")}
                        className="w-full px-4 lg:px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {expandedLessonSections.has("main-content") ? (
                            <ChevronDown className="w-5 h-5 text-slate-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-500" />
                          )}
                          <Video className="w-5 h-5 text-blue-600" />
                          <h3 className="text-base lg:text-lg font-semibold text-slate-900">
                            Video Content
                          </h3>
                        </div>
                        <span className="text-xs lg:text-sm text-slate-500 font-medium bg-blue-50 px-2 py-1 rounded-lg">
                          Video
                        </span>
                      </button>

                      {/* Collapsible Content */}
                      {expandedLessonSections.has("main-content") && (
                        <div className="px-4 lg:px-6 pb-6">
                          {selectedLesson.videoUrl ? (
                            (() => {
                              const videoUrl = selectedLesson.videoUrl;
                              const isYouTube = isYouTubeUrl(videoUrl);
                              const isVimeo = isVimeoUrl(videoUrl);

                              console.log("Video URL:", videoUrl);
                              console.log("Is YouTube:", isYouTube);
                              console.log("Is Vimeo:", isVimeo);

                              // For YouTube videos, use iframe embed
                              if (isYouTube) {
                                const embedUrl = toYouTubeEmbedUrl(videoUrl);

                                return (
                                  <div className="w-full">
                                    <div className="relative w-full pt-[56.25%] bg-slate-900 rounded-xl lg:rounded-2xl overflow-hidden shadow-lg ring-1 ring-slate-900/10">
                                      <iframe
                                        src={embedUrl}
                                        className="absolute top-0 left-0 w-full h-full"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        title="YouTube Video"
                                      />
                                    </div>
                                  </div>
                                );
                              }
                              // For Vimeo videos, use iframe embed
                              else if (isVimeo) {
                                const embedUrl = toVimeoEmbedUrl(videoUrl);

                                return (
                                  <div className="w-full">
                                    <div className="relative w-full pt-[56.25%] bg-slate-900 rounded-xl lg:rounded-2xl overflow-hidden shadow-lg ring-1 ring-slate-900/10">
                                      <iframe
                                        src={embedUrl}
                                        className="absolute top-0 left-0 w-full h-full"
                                        frameBorder="0"
                                        allow="autoplay; fullscreen; picture-in-picture"
                                        allowFullScreen
                                        title="Vimeo Video"
                                      />
                                    </div>
                                  </div>
                                );
                              } else {
                                // Use ReactPlayer for uploaded videos
                                return (
                                  <div className="w-full">
                                    {!videoLoadError ? (
                                      <div className="relative bg-slate-900 rounded-xl lg:rounded-2xl overflow-hidden shadow-lg ring-1 ring-slate-900/10">
                                        <ReactPlayer
                                          url={videoUrl}
                                          controls={true}
                                          width="100%"
                                          height="auto"
                                          style={{ aspectRatio: "16/9" }}
                                          onReady={() => {
                                            console.log("Video player ready");
                                            setVideoLoadError(false);
                                          }}
                                          onError={(error: any) => {
                                            console.error(
                                              "Video player error:",
                                              error
                                            );
                                            setVideoLoadError(true);
                                          }}
                                          config={{
                                            file: {
                                              attributes: {
                                                controlsList: "nodownload",
                                                className:
                                                  "w-full h-full object-contain",
                                              },
                                            },
                                          }}
                                        />
                                      </div>
                                    ) : (
                                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl lg:rounded-2xl border border-slate-200 p-8 lg:p-12">
                                        <div className="text-center">
                                          <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-200/80 mx-auto mb-4">
                                            <Video className="w-8 h-8 text-slate-400" />
                                          </div>
                                          <h4 className="text-lg font-semibold text-slate-900 mb-2">
                                            Video Unavailable
                                          </h4>
                                          <p className="text-sm text-slate-600 mb-6 max-w-sm mx-auto leading-relaxed">
                                            This video cannot be loaded. It may
                                            be due to CORS restrictions,
                                            unsupported format, or the video
                                            file is not accessible.
                                          </p>
                                          <a
                                            href={videoUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-xl hover:bg-slate-800 transition-all duration-200 shadow-sm hover:shadow-md"
                                          >
                                            <Video className="w-4 h-4" />
                                            Open Video in New Tab
                                          </a>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                            })()
                          ) : (
                            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl lg:rounded-2xl border border-slate-200 p-8 lg:p-12">
                              <div className="text-center">
                                <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-200/80 mx-auto mb-4">
                                  <Video className="w-8 h-8 text-slate-400" />
                                </div>
                                <h4 className="text-lg font-semibold text-slate-900 mb-2">
                                  No video available
                                </h4>
                                <p className="text-sm text-slate-600 max-w-sm mx-auto">
                                  This lesson does not have a playable video
                                  URL.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : selectedLesson.type === "document" ? (
                    <div className="bg-white rounded-xl lg:rounded-2xl border border-slate-200/60 overflow-hidden mb-6 shadow-sm">
                      {/* Collapsible Header */}
                      <button
                        onClick={() => toggleLessonSection("main-content")}
                        className="w-full px-4 lg:px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {expandedLessonSections.has("main-content") ? (
                            <ChevronDown className="w-5 h-5 text-slate-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-500" />
                          )}
                          <FileText className="w-5 h-5 text-emerald-600" />
                          <h3 className="text-base lg:text-lg font-semibold text-slate-900">
                            Document Content
                          </h3>
                        </div>
                        <span className="text-xs lg:text-sm text-slate-500 font-medium bg-emerald-50 px-2 py-1 rounded-lg">
                          Document
                        </span>
                      </button>

                      {/* Collapsible Content */}
                      {expandedLessonSections.has("main-content") && (
                        <div className="px-4 lg:px-6 pb-6">
                          {(() => {
                            // Check for document file in fileUrl or attachments
                            let documentUrl = selectedLesson.fileUrl;
                            let documentName = selectedLesson.title;

                            // If no direct fileUrl, check attachments for any file with a fileUrl
                            if (
                              !documentUrl &&
                              selectedLesson.attachments &&
                              selectedLesson.attachments.length > 0
                            ) {
                              // Look for any attachment that has a fileUrl (regardless of type)
                              const documentAttachment =
                                selectedLesson.attachments.find(
                                  (att) =>
                                    att.fileUrl && att.fileUrl.trim() !== ""
                                );

                              if (documentAttachment) {
                                documentUrl = documentAttachment.fileUrl;
                                documentName =
                                  documentAttachment.title ||
                                  selectedLesson.title;
                              }
                            }

                            console.log(
                              "Document lesson - fileUrl:",
                              selectedLesson.fileUrl
                            );
                            if (documentUrl) {
                              // Check if it's an external URL (might have CORS issues)
                              const isExternalUrl =
                                documentUrl.startsWith("http") &&
                                !documentUrl.includes("localhost") &&
                                !documentUrl.includes(window.location.hostname);

                              return (
                                <div>
                                  {/* Show a helpful message for external documents */}
                                  {isExternalUrl && (
                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/60 rounded-xl p-4 mb-4">
                                      <div className="flex items-start gap-3">
                                        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-100 flex-shrink-0">
                                          <FileText className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm text-blue-900 mb-3 leading-relaxed">
                                            This document is hosted externally.
                                            If the preview below doesn't load,
                                            click the button to open it in a new
                                            tab.
                                          </p>
                                          <a
                                            href={documentUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-all duration-200 shadow-sm hover:shadow-md"
                                          >
                                            <FileText className="w-4 h-4" />
                                            Open Document in New Tab
                                          </a>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Try iframe first for external documents, DocViewer for internal */}
                                  <div className="bg-white rounded-xl lg:rounded-2xl border border-slate-200/60 overflow-hidden shadow-sm">
                                    {isExternalUrl ? (
                                      <iframe
                                        src={documentUrl}
                                        className="w-full"
                                        style={{ height: "min(600px, 70vh)" }}
                                        title={documentName}
                                        onError={(e) => {
                                          console.error(
                                            "Iframe failed to load document:",
                                            e
                                          );
                                        }}
                                      />
                                    ) : (
                                      <DocViewer
                                        documents={[
                                          {
                                            uri: documentUrl,
                                            fileName: documentName,
                                          },
                                        ]}
                                        pluginRenderers={DocViewerRenderers}
                                        style={{ height: "min(600px, 70vh)" }}
                                        config={{
                                          header: {
                                            disableHeader: false,
                                            disableFileName: false,
                                            retainURLParams: false,
                                          },
                                        }}
                                      />
                                    )}
                                  </div>
                                </div>
                              );
                            } else {
                              return (
                                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl lg:rounded-2xl border border-slate-200 p-8 lg:p-12">
                                  <div className="text-center">
                                    <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-200/80 mx-auto mb-4">
                                      <FileText className="w-8 h-8 text-slate-400" />
                                    </div>
                                    <h4 className="text-lg font-semibold text-slate-900 mb-2">
                                      No document available
                                    </h4>
                                    <p className="text-sm text-slate-600 max-w-sm mx-auto">
                                      This lesson does not have a document file.
                                    </p>
                                  </div>
                                </div>
                              );
                            }
                          })()}
                        </div>
                      )}
                    </div>
                  ) : selectedLesson.type === "mixed" ? (
                    <div className="space-y-4">
                      {selectedLesson.contentItems &&
                      selectedLesson.contentItems.length > 0 ? (
                        selectedLesson.contentItems.map((item, index) => {
                          const itemId = item.id || `item-${index}`;
                          const isExpanded = expandedContentItems.has(itemId);

                          return (
                            <div
                              key={itemId}
                              className="bg-white rounded-xl lg:rounded-2xl border border-slate-200/60 overflow-hidden shadow-sm"
                            >
                              {/* Collapsible Header */}
                              <button
                                onClick={() =>
                                  toggleContentItemExpansion(itemId)
                                }
                                className="w-full px-4 lg:px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  {isExpanded ? (
                                    <ChevronDown className="w-5 h-5 text-slate-500" />
                                  ) : (
                                    <ChevronRight className="w-5 h-5 text-slate-500" />
                                  )}
                                  <div className="flex items-center gap-2">
                                    {item.type === "video" ||
                                    item.type === "audio" ? (
                                      <Video className="w-5 h-5 text-blue-600" />
                                    ) : item.type === "document" ? (
                                      <FileText className="w-5 h-5 text-emerald-600" />
                                    ) : item.type === "image" ? (
                                      <File className="w-5 h-5 text-purple-600" />
                                    ) : item.type === "link" ? (
                                      <LinkIcon className="w-5 h-5 text-orange-600" />
                                    ) : (
                                      <BookOpen className="w-5 h-5 text-slate-600" />
                                    )}
                                    <h4 className="text-base lg:text-lg font-semibold text-slate-900">
                                      {item.title ||
                                        `Content Item ${index + 1}`}
                                    </h4>
                                  </div>
                                </div>
                                <span className="text-xs lg:text-sm text-slate-500 font-medium capitalize bg-slate-100 px-2 py-1 rounded-lg">
                                  {item.type}
                                </span>
                              </button>

                              {/* Collapsible Content */}
                              {isExpanded && (
                                <div className="px-4 lg:px-6 pb-6">
                                  {/* Render based on item type */}
                                  {item.type === "text" && item.content ? (
                                    <div
                                      className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-headings:font-bold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-slate-600 prose-p:leading-relaxed prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-slate-900 prose-ul:text-slate-600 prose-ol:text-slate-600 prose-li:marker:text-slate-400 prose-blockquote:border-l-blue-500 prose-blockquote:text-slate-600 prose-blockquote:italic prose-code:text-pink-600 prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-img:rounded-lg prose-img:shadow-md"
                                      dangerouslySetInnerHTML={{
                                        __html: item.content,
                                      }}
                                    />
                                  ) : item.type === "text" ? (
                                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 p-8">
                                      <div className="text-center">
                                        <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-200/80 mx-auto mb-4">
                                          <FileText className="w-7 h-7 text-slate-400" />
                                        </div>
                                        <h4 className="text-lg font-semibold text-slate-900 mb-2">
                                          No content available
                                        </h4>
                                        <p className="text-sm text-slate-600">
                                          This text content item has no content.
                                        </p>
                                      </div>
                                    </div>
                                  ) : (item.type === "video" ||
                                      item.type === "audio") &&
                                    item.mediaUrl ? (
                                    <div className="mb-4">
                                      {(() => {
                                        const mediaUrl = item.mediaUrl;
                                        const isYouTube =
                                          isYouTubeUrl(mediaUrl);
                                        const isVimeo = isVimeoUrl(mediaUrl);

                                        console.log(
                                          "Mixed content media URL:",
                                          mediaUrl
                                        );
                                        console.log("Is YouTube:", isYouTube);
                                        console.log("Is Vimeo:", isVimeo);

                                        // For YouTube videos, use iframe embed
                                        if (isYouTube) {
                                          const embedUrl =
                                            toYouTubeEmbedUrl(mediaUrl);

                                          return (
                                            <div className="w-full">
                                              <div className="relative w-full pt-[56.25%] bg-slate-900 rounded-xl lg:rounded-2xl overflow-hidden shadow-lg ring-1 ring-slate-900/10">
                                                <iframe
                                                  src={embedUrl}
                                                  className="absolute top-0 left-0 w-full h-full"
                                                  frameBorder="0"
                                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                  allowFullScreen
                                                  title={item.title || "Video"}
                                                />
                                              </div>
                                            </div>
                                          );
                                        }
                                        // For Vimeo videos, use iframe embed
                                        else if (isVimeo) {
                                          const embedUrl =
                                            toVimeoEmbedUrl(mediaUrl);

                                          return (
                                            <div className="w-full">
                                              <div className="relative w-full pt-[56.25%] bg-slate-900 rounded-xl lg:rounded-2xl overflow-hidden shadow-lg ring-1 ring-slate-900/10">
                                                <iframe
                                                  src={embedUrl}
                                                  className="absolute top-0 left-0 w-full h-full"
                                                  frameBorder="0"
                                                  allow="autoplay; fullscreen; picture-in-picture"
                                                  allowFullScreen
                                                  title={
                                                    item.title || "Vimeo Video"
                                                  }
                                                />
                                              </div>
                                            </div>
                                          );
                                        } else {
                                          // Use ReactPlayer for uploaded videos
                                          return (
                                            <div className="w-full bg-slate-900 rounded-xl lg:rounded-2xl overflow-hidden shadow-lg ring-1 ring-slate-900/10">
                                              <ReactPlayer
                                                url={mediaUrl}
                                                controls={true}
                                                width="100%"
                                                height="auto"
                                                style={{ aspectRatio: "16/9" }}
                                                onReady={() =>
                                                  console.log(
                                                    "Mixed content video player ready"
                                                  )
                                                }
                                                onError={(error: any) =>
                                                  console.error(
                                                    "Mixed content video player error:",
                                                    error
                                                  )
                                                }
                                                config={{
                                                  file: {
                                                    attributes: {
                                                      controlsList:
                                                        "nodownload",
                                                    },
                                                  },
                                                }}
                                              />
                                            </div>
                                          );
                                        }
                                      })()}
                                    </div>
                                  ) : item.type === "video" ||
                                    item.type === "audio" ? (
                                    <div className="bg-slate-50 rounded-lg border p-6">
                                      <div className="text-center">
                                        <Video className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                                        <h4 className="text-lg font-medium text-slate-900 mb-2">
                                          No media available
                                        </h4>
                                        <p className="text-sm text-slate-600">
                                          This content item does not have a
                                          playable media URL.
                                        </p>
                                      </div>
                                    </div>
                                  ) : item.type === "link" &&
                                    item.externalUrl ? (
                                    <div className="mb-4">
                                      {(() => {
                                        const linkUrl = item.externalUrl;
                                        const isVideoLink =
                                          isYouTubeUrl(linkUrl) ||
                                          isVimeoUrl(linkUrl);

                                        // If it's a video link (YouTube, Vimeo, etc), show video player
                                        if (isVideoLink) {
                                          const isYouTube =
                                            isYouTubeUrl(linkUrl);
                                          const embedUrl = isYouTube
                                            ? toYouTubeEmbedUrl(linkUrl)
                                            : toVimeoEmbedUrl(linkUrl);
                                          return (
                                            <div className="w-full">
                                              <div className="relative w-full pt-[56.25%] bg-black rounded-lg overflow-hidden">
                                                <iframe
                                                  src={embedUrl}
                                                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                                                  frameBorder="0"
                                                  allow={
                                                    isYouTube
                                                      ? "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                      : "autoplay; fullscreen; picture-in-picture"
                                                  }
                                                  allowFullScreen
                                                  title={item.title || "Video"}
                                                />
                                              </div>
                                            </div>
                                          );
                                        } else {
                                          // For non-video links, show as button
                                          return (
                                            <a
                                              href={linkUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                            >
                                              <LinkIcon className="w-4 h-4" />
                                              Open Link: {item.title || linkUrl}
                                            </a>
                                          );
                                        }
                                      })()}
                                    </div>
                                  ) : item.type === "link" ? (
                                    <div className="bg-slate-50 rounded-lg border p-6">
                                      <div className="text-center">
                                        <LinkIcon className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                                        <h4 className="text-lg font-medium text-slate-900 mb-2">
                                          No link available
                                        </h4>
                                        <p className="text-sm text-slate-600">
                                          This content item does not have an
                                          external link.
                                        </p>
                                      </div>
                                    </div>
                                  ) : item.type === "document" ||
                                    item.type === "image" ||
                                    item.type === "interactive" ? (
                                    <div className="mb-4">
                                      {item.files && item.files.length > 0 ? (
                                        <div className="space-y-2">
                                          {item.files.map((file, fileIndex) => {
                                            const fileUrl = file.fileUrl;
                                            const fileName = file.fileName;
                                            const isExternalUrl =
                                              fileUrl.startsWith("http") &&
                                              !fileUrl.includes("localhost") &&
                                              !fileUrl.includes(
                                                window.location.hostname
                                              );

                                            return (
                                              <div
                                                key={fileIndex}
                                                className="space-y-3"
                                              >
                                                {/* Show a helpful message for external documents */}
                                                {isExternalUrl && (
                                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                    <div className="flex items-start gap-3">
                                                      <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                                                      <div className="flex-1">
                                                        <p className="text-sm text-blue-900 mb-2">
                                                          This document is
                                                          hosted externally. If
                                                          the preview below
                                                          doesn't load, click
                                                          the button to open it
                                                          in a new tab.
                                                        </p>
                                                        <a
                                                          href={fileUrl}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                                                        >
                                                          <FileText className="w-4 h-4" />
                                                          Open Document in New
                                                          Tab
                                                        </a>
                                                      </div>
                                                    </div>
                                                  </div>
                                                )}

                                                {/* Document/Image preview */}
                                                <div className="bg-white rounded-lg border overflow-hidden">
                                                  {item.type === "image" ? (
                                                    // Display images directly
                                                    <div className="p-4">
                                                      <img
                                                        src={fileUrl}
                                                        alt={fileName}
                                                        className="w-full h-auto rounded-lg"
                                                        onError={(e) => {
                                                          console.error(
                                                            "Failed to load image:",
                                                            e
                                                          );
                                                        }}
                                                      />
                                                    </div>
                                                  ) : isExternalUrl ? (
                                                    // Use iframe for external documents
                                                    <iframe
                                                      src={fileUrl}
                                                      className="w-full"
                                                      style={{ height: 400 }}
                                                      title={fileName}
                                                      onError={(e) => {
                                                        console.error(
                                                          "Iframe failed to load document:",
                                                          e
                                                        );
                                                      }}
                                                    />
                                                  ) : (
                                                    // Use DocViewer for internal documents
                                                    <DocViewer
                                                      documents={[
                                                        {
                                                          uri: fileUrl,
                                                          fileName: fileName,
                                                        },
                                                      ]}
                                                      pluginRenderers={
                                                        DocViewerRenderers
                                                      }
                                                      style={{ height: 400 }}
                                                      config={{
                                                        header: {
                                                          disableHeader: false,
                                                          disableFileName:
                                                            false,
                                                          retainURLParams:
                                                            false,
                                                        },
                                                      }}
                                                    />
                                                  )}
                                                </div>

                                                {/* Download button */}
                                                <div className="flex items-center gap-3 p-3 border rounded-lg">
                                                  <File className="w-5 h-5 text-slate-500" />
                                                  <div className="flex-1">
                                                    <p className="font-medium text-slate-900">
                                                      {fileName}
                                                    </p>
                                                    <p className="text-sm text-slate-600">
                                                      {formatFileSize(
                                                        file.fileSize
                                                      )}
                                                    </p>
                                                  </div>
                                                  <button
                                                    onClick={() => {
                                                      // Download file
                                                      const link =
                                                        document.createElement(
                                                          "a"
                                                        );
                                                      link.href = fileUrl;
                                                      link.download = fileName;
                                                      link.click();
                                                    }}
                                                    className="text-blue-600 hover:text-blue-700 p-1"
                                                    title="Download"
                                                  >
                                                    <Download className="w-4 h-4" />
                                                  </button>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      ) : (
                                        <div className="text-center py-8 text-slate-500">
                                          <File className="w-12 h-12 mx-auto mb-2" />
                                          <p>
                                            No files available for this content
                                            item
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="bg-slate-50 rounded-lg border p-6">
                                      <div className="text-center">
                                        <File className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                                        <h4 className="text-lg font-medium text-slate-900 mb-2">
                                          Unsupported content type
                                        </h4>
                                        <p className="text-sm text-slate-600">
                                          This content type ({item.type}) is not
                                          supported.
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="bg-slate-50 rounded-lg border p-6">
                          <div className="text-center">
                            <BookOpen className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                            <h4 className="text-lg font-medium text-slate-900 mb-2">
                              No content items available
                            </h4>
                            <p className="text-sm text-slate-600">
                              This mixed content lesson doesn't have any content
                              items.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border border-slate-100 overflow-hidden mb-6">
                      {/* Collapsible Header */}
                      <button
                        onClick={() => toggleLessonSection("main-content")}
                        className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {expandedLessonSections.has("main-content") ? (
                            <ChevronDown className="w-5 h-5 text-slate-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-500" />
                          )}
                          <BookOpen className="w-5 h-5 text-slate-600" />
                          <h3 className="text-lg font-semibold text-slate-900">
                            Lesson Content
                          </h3>
                        </div>
                        <span className="text-sm text-slate-500">Text</span>
                      </button>

                      {/* Collapsible Content */}
                      {expandedLessonSections.has("main-content") && (
                        <div className="px-4 lg:px-6 py-6">
                          <div
                            className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-headings:font-bold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-slate-600 prose-p:leading-relaxed prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-slate-900 prose-ul:text-slate-600 prose-ol:text-slate-600 prose-li:marker:text-slate-400 prose-blockquote:border-l-blue-500 prose-blockquote:text-slate-600 prose-blockquote:italic prose-code:text-pink-600 prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-img:rounded-lg prose-img:shadow-md"
                            dangerouslySetInnerHTML={{
                              __html: selectedLesson.content,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Lesson Attachments */}
                  {selectedLesson.attachments &&
                    selectedLesson.attachments.length > 0 && (
                      <div className="bg-white rounded-lg border overflow-hidden mb-6">
                        {/* Collapsible Header */}
                        <button
                          onClick={() => toggleLessonSection("attachments")}
                          className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {expandedLessonSections.has("attachments") ? (
                              <ChevronDown className="w-5 h-5 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-slate-500" />
                            )}
                            <File className="w-5 h-5 text-purple-600" />
                            <h3 className="text-lg font-semibold text-slate-900">
                              Attachments ({selectedLesson.attachments.length})
                            </h3>
                          </div>
                          <span className="text-sm text-slate-500">
                            {selectedLesson.attachments.length} file
                            {selectedLesson.attachments.length !== 1 ? "s" : ""}
                          </span>
                        </button>

                        {/* Collapsible Content */}
                        {expandedLessonSections.has("attachments") && (
                          <div className="px-6 pb-6">
                            <div className="space-y-3">
                              {selectedLesson.attachments.map((material) => {
                                const isAttachmentExpanded =
                                  expandedAttachments.has(material._id);

                                return (
                                  <div
                                    key={material._id}
                                    className="border rounded-lg overflow-hidden"
                                  >
                                    {/* Attachment Header */}
                                    <div className="flex items-center justify-between p-3 hover:bg-slate-50">
                                      <div className="flex items-center gap-3 flex-1">
                                        <button
                                          onClick={() =>
                                            toggleAttachment(material._id)
                                          }
                                          className="flex items-center gap-2"
                                        >
                                          {isAttachmentExpanded ? (
                                            <ChevronDown className="w-4 h-4 text-slate-500" />
                                          ) : (
                                            <ChevronRight className="w-4 h-4 text-slate-500" />
                                          )}
                                          {getMaterialIcon(material.type)}
                                        </button>
                                        <div className="flex-1">
                                          <h4 className="font-medium text-slate-900">
                                            {material.title}
                                          </h4>
                                          <p className="text-sm text-slate-600">
                                            {material.description}{" "}
                                            {formatFileSize(material.fileSize)}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() =>
                                            toggleAttachment(material._id)
                                          }
                                          className="text-purple-600 hover:text-purple-700 p-1"
                                          title={
                                            isAttachmentExpanded
                                              ? "Collapse"
                                              : "Expand preview"
                                          }
                                        >
                                          <Eye className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() =>
                                            downloadMaterial(material)
                                          }
                                          className="text-blue-600 hover:text-blue-700 p-1"
                                          title="Download"
                                        >
                                          <Download className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>

                                    {/* Attachment Preview */}
                                    {isAttachmentExpanded && (
                                      <div className="p-4 bg-slate-50 border-t">
                                        {(() => {
                                          // Helper function to check if file is an image
                                          const isImageFile = (
                                            url: string,
                                            type: string
                                          ) => {
                                            if (type === "image") return true;
                                            const imageExtensions = [
                                              ".jpg",
                                              ".jpeg",
                                              ".png",
                                              ".gif",
                                              ".bmp",
                                              ".webp",
                                              ".svg",
                                            ];
                                            return imageExtensions.some((ext) =>
                                              url.toLowerCase().endsWith(ext)
                                            );
                                          };

                                          // Helper function to check if file is a video
                                          const isVideoFile = (
                                            url: string,
                                            type: string
                                          ) => {
                                            if (type === "video") return true;
                                            const videoExtensions = [
                                              ".mp4",
                                              ".webm",
                                              ".ogg",
                                              ".mov",
                                              ".avi",
                                            ];
                                            return videoExtensions.some((ext) =>
                                              url.toLowerCase().endsWith(ext)
                                            );
                                          };

                                          // Helper function to check if file is a document
                                          const isDocumentFile = (
                                            type: string
                                          ) => {
                                            return (
                                              type === "pdf" ||
                                              type === "document"
                                            );
                                          };

                                          if (
                                            isImageFile(
                                              material.fileUrl,
                                              material.type
                                            )
                                          ) {
                                            return (
                                              <div className="bg-white rounded-lg p-4">
                                                <img
                                                  src={material.fileUrl}
                                                  alt={material.title}
                                                  className="w-full h-auto rounded-lg"
                                                  onError={(e) => {
                                                    console.error(
                                                      "Failed to load image:",
                                                      e
                                                    );
                                                  }}
                                                />
                                              </div>
                                            );
                                          } else if (
                                            isVideoFile(
                                              material.fileUrl,
                                              material.type
                                            )
                                          ) {
                                            const isYouTube = isYouTubeUrl(
                                              material.fileUrl
                                            );
                                            const isVimeo = isVimeoUrl(
                                              material.fileUrl
                                            );

                                            if (isYouTube) {
                                              const embedUrl =
                                                toYouTubeEmbedUrl(
                                                  material.fileUrl
                                                );
                                              return (
                                                <div className="w-full">
                                                  <div className="relative w-full pt-[56.25%] bg-black rounded-lg overflow-hidden">
                                                    <iframe
                                                      src={embedUrl}
                                                      className="absolute top-0 left-0 w-full h-full"
                                                      frameBorder="0"
                                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                      allowFullScreen
                                                      title={material.title}
                                                    />
                                                  </div>
                                                </div>
                                              );
                                            } else if (isVimeo) {
                                              const embedUrl = toVimeoEmbedUrl(
                                                material.fileUrl
                                              );
                                              return (
                                                <div className="w-full">
                                                  <div className="relative w-full pt-[56.25%] bg-black rounded-lg overflow-hidden">
                                                    <iframe
                                                      src={embedUrl}
                                                      className="absolute top-0 left-0 w-full h-full"
                                                      frameBorder="0"
                                                      allow="autoplay; fullscreen; picture-in-picture"
                                                      allowFullScreen
                                                      title={material.title}
                                                    />
                                                  </div>
                                                </div>
                                              );
                                            } else {
                                              return (
                                                <div className="w-full">
                                                  <ReactPlayer
                                                    url={material.fileUrl}
                                                    controls={true}
                                                    width="100%"
                                                    height="400px"
                                                    config={{
                                                      file: {
                                                        attributes: {
                                                          controlsList:
                                                            "nodownload",
                                                        },
                                                      },
                                                    }}
                                                  />
                                                </div>
                                              );
                                            }
                                          } else if (
                                            isDocumentFile(material.type)
                                          ) {
                                            const isExternalUrl =
                                              material.fileUrl.startsWith(
                                                "http"
                                              ) &&
                                              !material.fileUrl.includes(
                                                "localhost"
                                              ) &&
                                              !material.fileUrl.includes(
                                                window.location.hostname
                                              );

                                            return (
                                              <div className="bg-white rounded-lg border overflow-hidden">
                                                {isExternalUrl ? (
                                                  <div>
                                                    <div className="bg-blue-50 border-b border-blue-200 p-3">
                                                      <p className="text-sm text-blue-900">
                                                        External document -
                                                        <a
                                                          href={
                                                            material.fileUrl
                                                          }
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="ml-1 underline hover:text-blue-700"
                                                        >
                                                          Open in new tab
                                                        </a>
                                                      </p>
                                                    </div>
                                                    <iframe
                                                      src={material.fileUrl}
                                                      className="w-full"
                                                      style={{ height: 500 }}
                                                      title={material.title}
                                                    />
                                                  </div>
                                                ) : (
                                                  <DocViewer
                                                    documents={[
                                                      {
                                                        uri: material.fileUrl,
                                                        fileName:
                                                          material.title,
                                                      },
                                                    ]}
                                                    pluginRenderers={
                                                      DocViewerRenderers
                                                    }
                                                    style={{ height: 500 }}
                                                    config={{
                                                      header: {
                                                        disableHeader: false,
                                                        disableFileName: false,
                                                        retainURLParams: false,
                                                      },
                                                    }}
                                                  />
                                                )}
                                              </div>
                                            );
                                          } else if (material.type === "link") {
                                            return (
                                              <div className="bg-white rounded-lg p-4">
                                                <a
                                                  href={material.fileUrl}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                                >
                                                  <LinkIcon className="w-4 h-4" />
                                                  Open Link
                                                </a>
                                              </div>
                                            );
                                          } else {
                                            return (
                                              <div className="bg-white rounded-lg p-6">
                                                <div className="text-center text-slate-500">
                                                  <File className="w-12 h-12 mx-auto mb-2" />
                                                  <p className="text-sm">
                                                    Preview not available for
                                                    this file type
                                                  </p>
                                                  <button
                                                    onClick={() =>
                                                      downloadMaterial(material)
                                                    }
                                                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                                                  >
                                                    <Download className="w-4 h-4" />
                                                    Download File
                                                  </button>
                                                </div>
                                              </div>
                                            );
                                          }
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 mb-6">
                    <BookOpen className="w-10 h-10 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    Select a lesson to start learning
                  </h3>
                  <p className="text-slate-600 max-w-md">
                    Choose a lesson from the sidebar to view its content and
                    begin your learning journey.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "overview" &&
            (() => {
              console.log("Overview tab is active, course exists:", !!course);
              return (
                <div className="max-w-5xl mx-auto">
                  <h2 className="text-2xl font-bold text-slate-900 mb-8">
                    Course Overview
                  </h2>
                  <div className="space-y-6">
                    <div>
                      <div className="bg-white rounded-2xl border border-slate-200/60 p-8 mb-6 shadow-sm">
                        <h3 className="font-bold text-slate-900 mb-4 text-lg">
                          Description
                        </h3>
                        <p className="text-slate-600 leading-relaxed text-base">
                          {course.description}
                        </p>
                      </div>
                    </div>

                    <div>
                      <div className="bg-white rounded-2xl border border-slate-200/60 p-8 mb-6 shadow-sm">
                        <h3 className="font-bold text-slate-900 mb-6 text-lg">
                          Course Details
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div className="flex items-center gap-3 text-sm">
                            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-100 text-blue-600">
                              <Clock className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 font-medium">
                                Duration
                              </p>
                              <p className="font-semibold text-slate-900">
                                {typeof course.duration === "object"
                                  ? course.duration.estimatedHours
                                  : course.duration || 0}{" "}
                                hours
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-purple-100 text-purple-600">
                              <Users className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 font-medium">
                                Students
                              </p>
                              <p className="font-semibold text-slate-900">
                                {course.totalStudents} enrolled
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-yellow-100 text-yellow-600">
                              <Star className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 font-medium">
                                Rating
                              </p>
                              <div className="flex items-center gap-2">
                                <StarRating
                                  rating={course.rating}
                                  size="sm"
                                  readonly={true}
                                  showValue={false}
                                />
                                <span className="font-semibold text-slate-900">
                                  {course.rating.toFixed(1)} (
                                  {course.reviewCount})
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-green-100 text-green-600">
                              <Calendar className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 font-medium">
                                Enrolled
                              </p>
                              <p className="font-semibold text-slate-900">
                                {new Date(
                                  course.enrollmentDate
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl border border-slate-200/60 p-8 shadow-sm">
                        <h3 className="font-bold text-slate-900 mb-6 text-lg">
                          Instructor
                        </h3>
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-md overflow-hidden relative">
                            {course.instructor.profilePicture ? (
                              <img
                                src={course.instructor.profilePicture}
                                alt={`${course.instructor.firstName} ${course.instructor.lastName}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  const fallback =
                                    e.currentTarget.nextElementSibling;
                                  if (fallback instanceof HTMLElement) {
                                    fallback.classList.remove("hidden");
                                  }
                                }}
                              />
                            ) : null}
                            <div
                              className={`${
                                course.instructor.profilePicture ? "hidden" : ""
                              } absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center`}
                            >
                              <span className="text-2xl font-bold text-white">
                                {course.instructor.firstName[0]}
                              </span>
                            </div>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900 text-base">
                              {course.instructor.firstName}{" "}
                              {course.instructor.lastName}
                            </p>
                            <p className="text-sm text-slate-600 font-medium">
                              Course Instructor
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Reviews Section */}
                      <div className="bg-white rounded-2xl border border-slate-200/60 p-8 shadow-sm mt-6">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="font-bold text-slate-900 text-lg">
                            Reviews & Ratings
                          </h3>
                          {(() => {
                            const shouldShow =
                              (course?.progress?.percentage >= 100 ||
                                course?.enrollmentStatus === "completed") &&
                              !userReview;
                            console.log("Render time - Course:", course);
                            console.log(
                              "Render time - Progress:",
                              course?.progress
                            );
                            console.log(
                              "Render time - Enrollment status:",
                              course?.enrollmentStatus
                            );
                            console.log(
                              "Render time - User review:",
                              userReview
                            );
                            console.log(
                              "Render time - Should show button:",
                              shouldShow
                            );
                            return shouldShow ? (
                              <button
                                onClick={() => setShowReviewForm(true)}
                                className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                Write a Review
                              </button>
                            ) : null;
                          })()}
                        </div>

                        {ratingSummary && (
                          <div className="flex items-center mb-4">
                            <StarRating
                              rating={ratingSummary.averageRating || 0}
                              size="md"
                              readonly={true}
                              showValue={true}
                            />
                            <span className="ml-2 text-sm text-slate-600">
                              ({ratingSummary.totalReviews} reviews)
                            </span>
                          </div>
                        )}

                        {userReview && (
                          <div className="mb-4 p-3 border border-slate-200 rounded-lg">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="text-sm font-medium text-slate-900">
                                Your Review
                              </h4>
                              <button
                                onClick={() => setShowReviewForm(true)}
                                className="text-sm text-blue-600 hover:text-blue-800"
                              >
                                Edit
                              </button>
                            </div>
                            <div className="flex items-center mb-2">
                              <StarRating
                                rating={userReview.rating}
                                size="sm"
                                readonly={true}
                              />
                            </div>
                            <p className="text-sm text-slate-700">
                              {userReview.review}
                            </p>
                          </div>
                        )}

                        {showReviewForm && (
                          <div className="mb-4">
                            <ReviewForm
                              courseId={courseId!}
                              existingReview={userReview}
                              onSuccess={() => {
                                setShowReviewForm(false);
                                loadReviews();
                              }}
                              onCancel={() => setShowReviewForm(false)}
                            />
                          </div>
                        )}

                        {reviews.length > 0 ? (
                          <div className="space-y-3">
                            {reviews.slice(0, 3).map((review) => (
                              <div
                                key={review._id}
                                className="border-b border-slate-100 pb-3 last:border-b-0 last:pb-0"
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center">
                                    {review.user?.profilePicture ? (
                                      <img
                                        src={review.user.profilePicture}
                                        alt={`${review.user?.firstName} ${review.user?.lastName}`}
                                        className="w-6 h-6 rounded-full object-cover mr-2"
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).style.display = 'none';
                                          (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                                        }}
                                      />
                                    ) : null}
                                    <div className={`w-6 h-6 bg-slate-300 rounded-full flex items-center justify-center mr-2 ${review.user?.profilePicture ? 'hidden' : ''}`}>
                                      <span className="text-xs font-medium text-slate-700">
                                        {review.user?.firstName?.charAt(0) ||
                                          "U"}
                                      </span>
                                    </div>
                                    <div>
                                      <div className="text-xs font-medium text-slate-900">
                                        {review.user?.firstName}{" "}
                                        {review.user?.lastName}
                                      </div>
                                      <div className="text-xs text-slate-500">
                                        {new Date(
                                          review.createdAt
                                        ).toLocaleDateString()}
                                      </div>
                                    </div>
                                  </div>
                                  <StarRating
                                    rating={review.rating}
                                    size="sm"
                                    readonly={true}
                                  />
                                </div>
                                {review.review && (
                                  <p className="text-xs text-slate-700 mt-1">
                                    {review.review}
                                  </p>
                                )}
                              </div>
                            ))}
                            {reviews.length > 3 && (
                              <div className="text-center pt-2">
                                <button className="text-blue-600 hover:text-blue-800 text-xs font-medium">
                                  View all {reviews.length} reviews
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <Star className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                            <p className="text-xs text-slate-500">
                              No reviews yet. Be the first to review this
                              course!
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Certificate Section - Only show for completed courses */}
                      {course?.enrollmentStatus === "completed" && (
                        <div className="bg-white rounded-lg border p-6">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-slate-900 flex items-center">
                              <Award className="w-5 h-5 mr-2 text-green-600" />
                              Certificate of Completion
                            </h3>
                          </div>

                          {certificate ? (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center">
                                  <Award className="w-8 h-8 text-green-600 mr-3" />
                                  <div>
                                    <h4 className="font-medium text-green-800">
                                      Certificate Earned!
                                    </h4>
                                    <p className="text-sm text-green-600">
                                      Issued on{" "}
                                      {new Date(
                                        certificate.issuedAt
                                      ).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={downloadCertificate}
                                  className="flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                                >
                                  <Download className="w-4 h-4 mr-2" />
                                  Download PDF
                                </button>
                              </div>

                              <div className="text-sm text-slate-600">
                                <p>
                                  <strong>Certificate Number:</strong>{" "}
                                  {certificate.certificateNumber}
                                </p>
                                <p>
                                  <strong>Verification Code:</strong>{" "}
                                  {certificate.verificationCode}
                                </p>
                              </div>
                            </div>
                          ) : course?.chain && !course.chain.isChainFinal ? (
                            course.chain.nextCourseId ? (
                              <div className="text-center py-6">
                                <ChevronRight className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                                <h4 className="font-medium text-slate-900 mb-2">
                                  Continue Your Learning Path
                                </h4>
                                <p className="text-sm text-slate-600 mb-4">
                                  Great job completing this course! Continue
                                  your learning journey with the next course in
                                  this path.
                                </p>
                                <button
                                  onClick={() =>
                                    navigate(
                                      `/student/elearning/course/${course.chain.nextCourseId}`
                                    )
                                  }
                                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                                >
                                  <ChevronRight className="w-4 h-4" />
                                  Go to Next Course
                                </button>
                              </div>
                            ) : nextCourseInChain ? (
                              <div className="text-center py-6">
                                <ChevronRight className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                                <h4 className="font-medium text-slate-900 mb-2">
                                  Continue Your Learning Path
                                </h4>
                                <p className="text-sm text-slate-600 mb-4">
                                  Great job completing this course! Continue
                                  your learning journey with the next course in
                                  this path.
                                </p>
                                <button
                                  onClick={() =>
                                    navigate(
                                      `/student/elearning/course/${nextCourseInChain._id}`
                                    )
                                  }
                                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                                >
                                  <ChevronRight className="w-4 h-4" />
                                  Go to Next Course: {nextCourseInChain.title}
                                </button>
                              </div>
                            ) : (
                              <div className="text-center py-6">
                                <ChevronRight className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                                <h4 className="font-medium text-slate-900 mb-2">
                                  Learning Path Continues
                                </h4>
                                <p className="text-sm text-slate-600 mb-4">
                                  Great job completing this course! The next
                                  course in your learning path will be available
                                  soon.
                                </p>
                                <div className="px-6 py-2 bg-slate-100 text-slate-500 font-medium rounded-lg flex items-center gap-2 mx-auto w-fit">
                                  <ChevronRight className="w-4 h-4" />
                                  Next Course Coming Soon
                                </div>
                              </div>
                            )
                          ) : (
                            <div className="text-center py-6">
                              <Award className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                              <h4 className="font-medium text-slate-900 mb-2">
                                Generate Your Certificate
                              </h4>
                              <p className="text-sm text-slate-600 mb-4">
                                Congratulations on completing this course!
                                Generate your certificate of completion.
                              </p>
                              <button
                                onClick={generateCertificate}
                                disabled={isGeneratingCertificate}
                                className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isGeneratingCertificate ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Generating...
                                  </>
                                ) : (
                                  <>
                                    <Award className="w-4 h-4 mr-2" />
                                    Generate Certificate
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

          {activeTab === "discussions" && courseId && (
            <div className="px-4 lg:px-8 py-6 lg:py-8 max-w-5xl mx-auto">
              <CourseDiscussionBoard courseId={courseId} />
            </div>
          )}
        </div>
      </div>

      {/* Document Preview Modal */}
      {previewDocument && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200/60 bg-gradient-to-r from-slate-50 to-white">
              <h3 className="text-lg font-semibold text-slate-900">
                {previewDocument.title}
              </h3>
              <button
                onClick={() => setPreviewDocument(null)}
                className="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <DocViewer
                documents={[
                  {
                    uri: previewDocument.fileUrl,
                    fileName: previewDocument.title,
                  },
                ]}
                pluginRenderers={DocViewerRenderers}
                style={{ height: "min(600px, 65vh)" }}
                config={{
                  header: {
                    disableHeader: false,
                    disableFileName: false,
                    retainURLParams: false,
                  },
                }}
              />
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200/60 bg-slate-50/50">
              <button
                onClick={() => setPreviewDocument(null)}
                className="px-4 py-2.5 bg-slate-100 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-200 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => downloadMaterial(previewDocument)}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-sm"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentCourseViewer;
